import { FileUp, Layers, Network } from 'lucide-react';

import {
  formatProvenanceSummary,
  ProvenanceNode,
} from '@/sections/seeds/utils/provenanceProcessing';
import { cn } from '@/utils/classname';
import { formatDate } from '@/utils/date.util';

interface ProvenanceSectionProps {
  provenanceChain: ProvenanceNode[];
  originationInfo: string[];
}

function KindIcon({ kind }: { kind: ProvenanceNode['kind'] }) {
  const cls = 'size-4 shrink-0';
  switch (kind) {
    case 'preseed':
      return <Layers className={cls} />;
    case 'integration':
      return <FileUp className={cls} />;
    default:
      return <Network className={cls} />;
  }
}

export function ProvenanceSection({
  provenanceChain,
  originationInfo,
}: ProvenanceSectionProps) {
  if (provenanceChain.length === 0 && originationInfo.length === 0) return null;

  if (provenanceChain.length === 0) {
    return (
      <div className="border-chariot-stroke mt-6 border-t pt-6">
        <div className="text-chariot-text-secondary flex flex-col gap-1 text-sm">
          {originationInfo.map((info, index) => (
            <div key={index} className="flex items-center gap-2">
              <span className="bg-chariot-stroke size-2 rounded-full" />
              {info}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="border-chariot-stroke border-t pt-6">
      <h3 className="text-chariot-text-primary mb-3 text-sm font-semibold">
        Discovery chain
      </h3>
      <div className="relative ml-3">
        <div className="bg-chariot-stroke absolute top-0 bottom-0 left-0 w-px" />

        {provenanceChain.map((node, i) => {
          const prev = i > 0 ? provenanceChain[i - 1] : undefined;
          return (
            <div key={node.key ?? i} className="relative mb-4 pl-5 last:mb-0">
              <div
                className={cn(
                  'absolute top-1 -left-1.5 flex size-3 items-center justify-center rounded-full',
                  'bg-chariot-brand-primary'
                )}
              />

              <div className="border-chariot-border-default rounded-lg border p-3">
                <div className="flex items-start gap-2.5">
                  <KindIcon kind={node.kind} />
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <p className="text-chariot-text-primary text-sm">
                      {formatProvenanceSummary(node, prev)}
                    </p>
                    {node.created && (
                      <p className="text-chariot-text-secondary text-xs">
                        {formatDate(node.created)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
